import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { tournamentDayApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";

function displayReference(hash: string): string | null {
  return new URLSearchParams(hash.replace(/^#/, "")).get("ref");
}

/**
 * Chrome-less one-time exchange route. The BFF consumes the backend capability
 * and leaves the browser with only an HttpOnly display cookie before this page
 * replaces the URL, so the setup reference cannot survive in history/storage.
 */
export function VenueDisplayExchangePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut } = useAuth();
  const locationKey = `${location.key}:${location.pathname}:${location.search}:${location.hash}`;
  const referenceForLocation = useRef<{
    key: string;
    value: string | null;
  } | null>(null);
  if (referenceForLocation.current?.key !== locationKey) {
    referenceForLocation.current = {
      key: locationKey,
      value: displayReference(location.hash),
    };
  }
  const reference = referenceForLocation.current?.value ?? null;
  const startedFor = useRef<string | null>(null);
  const exchangeIdempotencyKey = useRef<string | null>(null);
  const exchange = useMutation({
    mutationFn: tournamentDayApi.exchangeTournamentDisplay,
    retry: false,
    onSuccess: async (grant) => {
      // The BFF has already deleted the normal session before returning this
      // success response. Remove the same browser's in-memory authority and
      // every cached user-scoped response before the TV enters its device-only
      // route. `signOut` is deliberately best-effort here: the BFF conversion
      // is complete, and its finally block still clears local Firebase/React
      // state if an already-invalid logout request cannot reach the network.
      queryClient.clear();
      try {
        await signOut();
      } catch {
        // The local `signOut` cleanup ran in its finally block. Do not strand a
        // successfully provisioned display on a now-irrelevant logout error.
      }
      queryClient.clear();
      navigate(`/tournament-display/${grant.tournament_id}`, { replace: true });
    },
  });

  useLayoutEffect(() => {
    const search = new URLSearchParams(location.search);
    const hasLegacyReference = search.has("ref");
    const hasFragmentReference = new URLSearchParams(
      location.hash.replace(/^#/, ""),
    ).has("ref");
    if (!hasLegacyReference && !hasFragmentReference) return;
    search.delete("ref");
    // Scrub the one-time fragment before the passive exchange effect can make
    // any network request or error telemetry call. A rejected legacy `?ref=`
    // gets the same immediate address-bar/history cleanup as a valid fragment.
    const nextSearch = search.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`,
    );
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (!reference || startedFor.current === reference) return;
    startedFor.current = reference;
    const idempotencyKey = crypto.randomUUID();
    exchangeIdempotencyKey.current = idempotencyKey;
    exchange.mutate({
      exchange_reference: reference,
      idempotency_key: idempotencyKey,
      device_name: "Venue display",
    });
  }, [exchange, reference]);

  const retryExchange = () => {
    if (!reference || exchange.isPending) return;
    const idempotencyKey =
      exchangeIdempotencyKey.current ?? crypto.randomUUID();
    exchangeIdempotencyKey.current = idempotencyKey;
    exchange.reset();
    startedFor.current = reference;
    exchange.mutate({
      exchange_reference: reference,
      idempotency_key: idempotencyKey,
      device_name: "Venue display",
    });
  };

  const status = (exchange.error as { status?: unknown } | undefined)?.status;
  const invalid = status === 401 || status === 403 || !reference;

  return (
    <Box
      component="main"
      sx={{
        alignItems: "center",
        bgcolor: "grey.900",
        color: "common.white",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        p: 3,
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 520, textAlign: "center" }}>
        {exchange.isError || !reference ? (
          <Stack spacing={1.5} sx={{ alignItems: "center" }}>
            <Alert severity={invalid ? "warning" : "error"} role="alert">
              {invalid
                ? "This display setup link is invalid or expired. Ask an organizer for a new link."
                : "The display could not be connected just now."}
            </Alert>
            {!invalid ? (
              <Button
                color="inherit"
                onClick={retryExchange}
                variant="outlined"
              >
                Try again
              </Button>
            ) : null}
          </Stack>
        ) : (
          <>
            <Typography component="h1" variant="h4">
              Connecting display
            </Typography>
            <Stack
              direction="row"
              role="status"
              spacing={1}
              sx={{ alignItems: "center", justifyContent: "center" }}
            >
              <CircularProgress color="inherit" size={24} />
              <Typography>
                Establishing the read-only display session…
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
